import { AlertCircle, Eye, EyeOff, Loader2, Lock } from "lucide-react";
import { useState } from "react";
import { FS, FU } from "../components/dashboard/tokens";
import { clearResumeToken, getResumeToken, isAccessAdminPath, setSession } from "../lib/auth";
import { APP_NAME, AUTHORITY, COUNTRY_ADJ } from "../lib/tenant";

// Sign-in screen. A single, continuous dark "world" (independent of the app
// theme — like the landing hub): the brand hero on the left and the form on the
// right both float on the same navy gradient. Posts to /api/v1/auth/login,
// stores the token, and reloads into the dashboard.
//
// Colours are fixed dark-world constants (mirroring landing-hub.tsx) rather than
// the themeable `C` tokens, so the screen reads identically regardless of the
// dashboard's light/dark setting.

const FM = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";
const INK = "#E8EEFB"; //     primary text
const DIM = "#AEB7CC"; //     labels / secondary text
const FAINT = "#5C6680"; //   placeholders / faint mono
const GOLD = "#E9C977"; //    hero accent word
const COBALT = "#82BAFF"; //  accent (icons, focus, eyebrow)
const CRISIS = "#FF8080"; //  error (legible red on dark)

interface LoginError {
  code?: string;
  message?: string;
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const res = await fetch("/api/v1/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password, resumeToken: getResumeToken() ?? undefined }),
      });
      if (!res.ok) {
        const payload = (await res.json().catch(() => null)) as { error?: LoginError } | null;
        setError(payload?.error?.message ?? "Login failed. Please try again.");
        setBusy(false);
        return;
      }
      const data = (await res.json()) as { token: string; email: string; canLogout: boolean; kind: "user" | "code" };
      setSession(data.token, data.email, { canLogout: data.canLogout, kind: data.kind });
      clearResumeToken();
      // Full navigation so the app re-mounts with the token in place. The admin
      // page renders this form in place at its own URL (main.tsx), so stay on
      // it after sign-in; everywhere else keeps landing on the dashboard.
      const here = window.location.pathname;
      window.location.href = isAccessAdminPath(here) ? here + window.location.hash : "/";
    } catch {
      setError("Could not reach the server. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="lg-root">
      {/* Full-screen ambient texture (behind both panes). */}
      <div className="lg-glow" aria-hidden="true" />
      <div className="lg-grid" aria-hidden="true" />

      <div className="lg-cols">
        {/* ── Brand hero ────────────────────────────────────────────────── */}
        <aside className="lg-brand" aria-hidden="true">
          <div className="lg-wordmark">{APP_NAME}</div>
          <div className="lg-brand-hero">
            <div className="lg-eyebrow" style={{ color: DIM }}>
              {AUTHORITY} · Demo
            </div>
            <h2 className="lg-hero-title">
              A demand sensing agent for <em>{COUNTRY_ADJ} tourism</em>
            </h2>
            <p className="lg-hero-sub">From millions of signals into structured go-to-market plans.</p>
          </div>
        </aside>

        {/* ── Form ──────────────────────────────────────────────────────── */}
        <main className="lg-form-pane">
          <div className="lg-form-col">
            <div className="lg-mark">
              <Lock size={18} strokeWidth={2} aria-hidden="true" />
            </div>

            <div className="lg-eyebrow lg-form-eyebrow">Sign in</div>
            <h1 className="lg-title">Welcome back</h1>
            <p className="lg-subtitle">Use the email and password you were given to access the dashboard.</p>

            <form onSubmit={submit} className="lg-form" noValidate>
              <div className="lg-field">
                <label htmlFor="lg-email" className="lg-label">
                  Email
                </label>
                <input
                  id="lg-email"
                  type="email"
                  autoComplete="username"
                  // biome-ignore lint/a11y/noAutofocus: a login form is the sole purpose of the page; focusing the first field is expected.
                  autoFocus
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  placeholder="Email"
                  required
                  className="lg-input"
                  aria-invalid={error ? true : undefined}
                />
              </div>

              <div className="lg-field">
                <label htmlFor="lg-password" className="lg-label">
                  Password
                </label>
                <div className="lg-input-wrap">
                  <input
                    id="lg-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(ev) => setPassword(ev.target.value)}
                    placeholder="Password"
                    required
                    className="lg-input lg-input-pw"
                    aria-invalid={error ? true : undefined}
                  />
                  <button
                    type="button"
                    className="lg-eye"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} strokeWidth={2} /> : <Eye size={16} strokeWidth={2} />}
                  </button>
                </div>
              </div>

              {/* Reserved slot — always occupies space so showing/clearing the
                  error never shifts the button or re-centers the form. */}
              <div className="lg-error-slot" role="alert" aria-live="polite">
                {error && (
                  <span className="lg-error">
                    <AlertCircle size={14} strokeWidth={2} aria-hidden="true" />
                    {error}
                  </span>
                )}
              </div>

              <button type="submit" className="lg-submit" disabled={busy}>
                {busy ? (
                  <>
                    <Loader2 size={15} strokeWidth={2.4} className="lg-spin" aria-hidden="true" />
                    Signing in…
                  </>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>

            <p className="lg-footnote">Restricted access · Invited users only</p>
          </div>
        </main>
      </div>

      <style>{`
        .lg-root {
          position: relative;
          min-height: 100vh;
          overflow: hidden;
          font-family: ${FU};
          color: ${INK};
          background:
            radial-gradient(120% 90% at 12% 8%, #182138 0%, rgba(24,33,56,0) 55%),
            linear-gradient(150deg, #0e1220 0%, #0a0d17 100%);
        }
        .lg-glow {
          position: absolute; top: -18%; left: -6%; width: 46%; height: 58%;
          background: radial-gradient(circle, ${COBALT}20 0%, transparent 70%);
          filter: blur(20px); pointer-events: none;
        }
        .lg-grid {
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 44px 44px;
          mask-image: radial-gradient(120% 100% at 16% 26%, #000 0%, transparent 68%);
          -webkit-mask-image: radial-gradient(120% 100% at 16% 26%, #000 0%, transparent 68%);
          pointer-events: none;
        }

        .lg-cols {
          position: relative; z-index: 1;
          min-height: 100vh;
          display: grid;
          grid-template-columns: 1.05fr 1fr;
        }

        /* ── Brand hero ─────────────────────────────────────────────────── */
        .lg-brand {
          position: relative;
          display: flex; flex-direction: column; justify-content: center;
          padding: 44px 56px;
        }
        .lg-wordmark {
          position: absolute; top: 44px; left: 56px;
          font-family: ${FM}; font-size: 12px; letter-spacing: 0.22em;
          text-transform: uppercase;
          color: ${FAINT}; font-weight: 500;
        }
        .lg-brand-hero { max-width: 460px; }
        .lg-eyebrow {
          font-family: ${FM}; font-size: 11px; font-weight: 500;
          letter-spacing: 0.2em; text-transform: uppercase;
        }
        .lg-hero-title {
          font-family: ${FS}; font-weight: 400;
          font-size: clamp(30px, 3.6vw, 46px); line-height: 1.12;
          color: ${INK}; margin: 18px 0 0; letter-spacing: -0.01em;
        }
        .lg-hero-title em { font-style: italic; color: ${GOLD}; }
        .lg-hero-sub {
          font-size: 15px; line-height: 1.6; color: ${DIM};
          margin: 18px 0 0; max-width: 380px;
        }

        /* ── Form ───────────────────────────────────────────────────────── */
        .lg-form-pane {
          display: flex; align-items: center; justify-content: center;
          padding: 40px 24px;
        }
        .lg-form-col { width: 100%; max-width: 360px; }
        .lg-mark {
          width: 44px; height: 44px; border-radius: 12px;
          display: inline-flex; align-items: center; justify-content: center;
          background: rgba(130,186,255,0.12); color: ${COBALT};
          margin-bottom: 22px;
        }
        .lg-form-eyebrow { color: ${COBALT}; font-family: ${FU}; letter-spacing: 0.16em; }
        .lg-title {
          font-family: ${FS}; font-weight: 400; font-size: 28px;
          color: ${INK}; margin: 8px 0 0; letter-spacing: -0.01em;
        }
        .lg-subtitle { font-size: 13px; line-height: 1.55; color: ${DIM}; margin: 8px 0 0; }

        .lg-form { display: flex; flex-direction: column; gap: 15px; margin-top: 28px; }
        .lg-field { display: flex; flex-direction: column; gap: 7px; }
        .lg-label { font-size: 12px; font-weight: 500; color: ${DIM}; }
        .lg-input-wrap { position: relative; display: flex; }
        .lg-input {
          width: 100%; box-sizing: border-box;
          background: rgba(255,255,255,0.055);
          border: 1px solid rgba(255,255,255,0.09);
          border-radius: 8px; padding: 11px 13px;
          font-size: 13px; font-family: ${FU}; color: ${INK};
          outline: none;
          transition: background 140ms ease, border-color 140ms ease, box-shadow 140ms ease;
        }
        .lg-input-pw { padding-right: 42px; }
        .lg-input::placeholder { color: ${FAINT}; }
        .lg-input:hover { background: rgba(255,255,255,0.08); }
        .lg-input:focus {
          background: rgba(255,255,255,0.09);
          border-color: ${COBALT};
          box-shadow: 0 0 0 3px rgba(130,186,255,0.16);
        }
        .lg-input[aria-invalid="true"] { border-color: rgba(255,128,128,0.6); }
        /* Autofill repaint — keep the dark field + light text. */
        .lg-input:-webkit-autofill,
        .lg-input:-webkit-autofill:hover,
        .lg-input:-webkit-autofill:focus {
          -webkit-text-fill-color: ${INK};
          -webkit-box-shadow: 0 0 0 1000px #171d2c inset;
          caret-color: ${INK};
          transition: background-color 9999s ease-in-out 0s;
        }
        .lg-eye {
          position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
          width: 30px; height: 30px;
          display: inline-flex; align-items: center; justify-content: center;
          background: transparent; border: none; border-radius: 6px;
          color: ${FAINT}; cursor: pointer;
          transition: color 140ms ease, background 140ms ease;
        }
        .lg-eye:hover { color: ${INK}; background: rgba(255,255,255,0.06); }
        .lg-eye:focus-visible { outline: 2px solid ${COBALT}; outline-offset: 1px; }

        .lg-error-slot { min-height: 18px; margin: -3px 0 -1px; display: flex; align-items: center; }
        .lg-error {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 12px; line-height: 1.35; color: ${CRISIS};
          animation: lg-slide-in 160ms ease-out;
        }
        .lg-error svg { flex-shrink: 0; }

        .lg-submit {
          width: 100%; height: 44px; margin-top: 4px;
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          border: none; border-radius: 8px; cursor: pointer;
          font-family: ${FU}; font-size: 13px; font-weight: 600; color: #fff;
          background: linear-gradient(180deg, #4d7dff 0%, #3457e6 100%);
          box-shadow: 0 6px 18px rgba(52,87,230,0.32);
          transition: filter 140ms ease, transform 60ms ease, box-shadow 140ms ease;
        }
        .lg-submit:hover { filter: brightness(1.07); box-shadow: 0 8px 22px rgba(52,87,230,0.42); }
        .lg-submit:active { transform: translateY(1px); }
        .lg-submit:disabled { opacity: 0.65; cursor: default; filter: none; box-shadow: none; }
        .lg-submit:focus-visible { outline: 2px solid ${COBALT}; outline-offset: 2px; }
        .lg-spin { animation: lg-spin 700ms linear infinite; }

        .lg-footnote { font-size: 11px; color: ${FAINT}; margin: 22px 0 0; text-align: center; }

        @keyframes lg-spin { to { transform: rotate(360deg); } }
        @keyframes lg-slide-in {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Below 860px: drop the hero, center the form on the same dark bg. */
        @media (max-width: 860px) {
          .lg-cols { grid-template-columns: 1fr; }
          .lg-brand { display: none; }
        }
        @media (prefers-reduced-motion: reduce) {
          .lg-spin, .lg-error { animation: none; }
        }
      `}</style>
    </div>
  );
}
